import React, { useEffect, useState } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { IconButton, Menu, MenuItem, Typography, Alert, AlertTitle, Box, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { useSnackbar } from "hooks/useSnackbar";
import CustomButton from "components/CustomButton";
import configData from "config/config.json";
import {
  getVaultRewardConfigs,
  deleteVaultRewardConfig,
} from "services/vaultRewardConfigService";
import { getConfigurations, createAndUpdateConfig } from "services/miscService";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate, useSearchParams } from "react-router-dom";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import useConsent from "hooks/useConsent";
import { sortArray } from "utils/miscUtils";
import SearchInput from "components/SearchInput";

const RowActions = ({ data, actionHandlers }: any) => {
  const { handleDelete = () => { }, handleEdit = () => { } } = actionHandlers;
  const [anchorEl, setAnchorEl] = useState(null);

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.VAULT_REWARDS_AND_DROP_RATES.UPDATE_ROLES
  );
  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.VAULT_REWARDS_AND_DROP_RATES.DELETE_ROLES
  );

  const handleClick = (event: any) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleRowActions = (data, action) => {
    setAnchorEl(null);
    if (action === "edit") {
      handleEdit(data?._id);
    } else {
      handleDelete(data);
    }
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {hasEditPermission && (
          <MenuItem onClick={() => handleRowActions(data, "edit")}>
            Edit
          </MenuItem>
        )}
        {hasDeletePermission && (
          <MenuItem onClick={() => handleRowActions(data, "delete")}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const VaultRewardConfigList = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [orderDirection, setOrderDirection] = useState("desc");
  const [searchValue, setSearchValue] = useState(searchParams.get("level") || "");
  const [maxVaultLevel, setMaxVaultLevel] = useState(50);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMaxVaultLevel, setEditMaxVaultLevel] = useState(50);
  const [vaultConfiguration, setVaultConfiguration] = useState(null);
  const [saving, setSaving] = useState(false);
  const {
    updateDetails,
    openConsent,
    closeConsent,
    constentDetails,
    isOpenConsent,
  } = useConsent();

  const columns = [
    {
      id: "level",
      label: "Level",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
    },
    {
      id: "playerRewardCount",
      label: "Player Reward Count",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      customRender: (row) => (
        <Typography
          variant="body2"
          style={{ color: "#2e7d32", fontWeight: "bold" }}
        >
          {row.playerRewardCount}
        </Typography>
      ),
    },
    {
      id: "coinRewardCount",
      label: "Coin Reward Count",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      customRender: (row) => (
        <Typography
          variant="body2"
          style={{ color: "#f57c00", fontWeight: "bold" }}
        >
          {row.coinRewardCount}
        </Typography>
      ),
    },
    {
      id: "boosterRewardCount",
      label: "Booster Reward Count",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      customRender: (row) => (
        <Typography
          variant="body2"
          style={{ color: "#1976d2", fontWeight: "bold" }}
        >
          {row.boosterRewardCount}
        </Typography>
      ),
    },
    {
      id: "totalProbability",
      label: "Total Probability (%)",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      customRender: (row) => (
        <Typography
          variant="body2"
          style={{
            color: row.totalProbability === 100 ? "green" : "red",
            fontWeight: row.totalProbability === 100 ? "normal" : "bold",
          }}
        >
          {row.totalProbability}%
        </Typography>
      ),
    },
    { id: "actions", label: "Actions", alignHead: "center" },
  ];

  const fetchMaxVaultLevel = async () => {
    try {
      const miscResp = await getConfigurations();
      const vaultConfig = miscResp?.data?.find(
        (d) => d.type === "VAULT_CONFIGURATION"
      );
      const maxLevel = vaultConfig?.data?.maxVaultLevel ?? 50;
      setMaxVaultLevel(maxLevel);
      setEditMaxVaultLevel(maxLevel);
      setVaultConfiguration(vaultConfig);
      return maxLevel;
    } catch (error) {
      console.error("Error fetching vault configuration:", error);
      return 50; // Default fallback
    }
  };

  const validateVaultData = (items, maxLevel) => {
    const warnings = [];
    const existingLevels = items.map(item => item.level);
    const levelSet = new Set(existingLevels);

    // Check for missing levels (0 to maxLevel)
    const missingLevels = [];
    for (let i = 0; i <= maxLevel; i++) {
      if (!levelSet.has(i)) {
        missingLevels.push(i);
      }
    }

    // Check for duplicate levels
    const duplicateLevels = [];
    const seen = new Set();
    for (const level of existingLevels) {
      if (seen.has(level)) {
        if (!duplicateLevels.includes(level)) {
          duplicateLevels.push(level);
        }
      } else {
        seen.add(level);
      }
    }

    // Check for levels outside valid range
    const invalidLevels = existingLevels.filter(level => level < 0 || level > maxLevel);

    if (missingLevels.length > 0) {
      warnings.push(`Missing levels: ${missingLevels.join(", ")}`);
    }

    if (duplicateLevels.length > 0) {
      warnings.push(`Duplicate levels found: ${duplicateLevels.join(", ")}`);
    }

    if (invalidLevels.length > 0) {
      warnings.push(`Invalid levels (outside 0-${maxLevel} range): ${invalidLevels.join(", ")}`);
    }

    setValidationWarnings(warnings);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch max vault level first
      const maxLevel = await fetchMaxVaultLevel();

      const payload = {
        pageNo: 1,
        limit: 1000,
        sortKey: "level",
        sortDir: "ASC",
        skipPagination: true
      };

      const resp = await getVaultRewardConfigs(payload);
      const items = resp?.data?.items || [];

      // Process items to add calculated fields
      const processedItems = items.map((item) => ({
        ...item,
        playerRewardCount:
          item.rewards?.filter((r) => r.slotType === "Player").length || 0,
        coinRewardCount:
          item.rewards?.filter((r) => r.slotType === "Coins").length || 0,
        boosterRewardCount:
          item.rewards?.filter((r) => r.slotType === "Booster").length || 0,
        totalProbability:
          item.rewards?.reduce((sum, r) => sum + (r.probability || 0), 0) || 0,
      }));

      // Validate the data
      validateVaultData(processedItems, maxLevel);

      setAllItems(processedItems);
      filterItems(processedItems, searchValue);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const filterItems = (items, searchTerm) => {
    if (!searchTerm) {
      setFilteredList(items);
      return;
    }

    const filtered = items.filter((item) => {
      const levelString = item.level?.toString() || "";
      return levelString === searchTerm;
    });

    setFilteredList(filtered);
  };

  const handleEdit = (id: any) => {
    navigate(
      configData.routes.vaultRewardsAndDropRates.edit?.replace(":id", id)
    );
  };

  const handleDelete = (data: any) => {
    if (!data?._id) {
      showSnackbar("Unable to delete: Item ID not found", "error");
      return;
    }

    updateDetails({ itemName: `Level ${data?.level}`, id: data?._id });
    openConsent();
  };

  const onConfirmDelete = async () => {
    try {
      const vaultRewardConfigId = constentDetails?.id;

      if (!vaultRewardConfigId) {
        showSnackbar("No item selected for deletion", "error");
        closeConsent();
        return;
      }

      let response = await deleteVaultRewardConfig(vaultRewardConfigId);
      fetchData();
      closeConsent();
      showSnackbar(response?.message || "Deleted successfully", "success");
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const createVaultRewardConfig = () =>
    navigate(configData.routes.vaultRewardsAndDropRates.create);

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const onSearch = (value: any) => {
    setSearchValue(value);

    // Update query params
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) {
      newSearchParams.set("level", value);
    } else {
      newSearchParams.delete("level");
    }
    setSearchParams(newSearchParams);

    // Filter items based on search value
    filterItems(allItems, value);
  };

  const handleEditMaxVaultLevel = () => {
    setEditMaxVaultLevel(maxVaultLevel);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditMaxVaultLevel(maxVaultLevel);
  };

  const handleSaveMaxVaultLevel = async () => {
    if (!vaultConfiguration || !editMaxVaultLevel || editMaxVaultLevel < 0) {
      showSnackbar("Please enter a valid max vault level (>= 0)", "error");
      return;
    }

    setSaving(true);
    try {
      // Create payload using the same structure as received from getConfigurations
      const payload = {
        type: "VAULT_CONFIGURATION",
        data: {
          ...vaultConfiguration.data,
          maxVaultLevel: parseInt(String(editMaxVaultLevel), 10)
        }
      };

      await createAndUpdateConfig(payload);

      // Update local state
      setMaxVaultLevel(parseInt(String(editMaxVaultLevel), 10));
      setEditDialogOpen(false);

      // Re-validate data with new max level
      validateVaultData(allItems, parseInt(String(editMaxVaultLevel), 10));

      showSnackbar("Max vault level updated successfully", "success");
    } catch (error) {
      showSnackbar(error.message || "Failed to update max vault level", "error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Handle initial search from URL params
    if (searchValue) {
      filterItems(allItems, searchValue);
    }
  }, [allItems, searchValue]);

  return (
    <>
      <ContentShell
        title="Vault Reward Configurations"
        loading={loading}
        actions={
          <div style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
            <SearchInput
              placeholder="Search by level..."
              onSearch={onSearch}
              value={searchValue}
            />
            {sessionHelper.hasRole(
              PermittedRoles.VAULT_REWARDS_AND_DROP_RATES.CREATE_ROLES
            ) && (
                <CustomButton onClick={createVaultRewardConfig}>
                  Create Vault Reward Config
                </CustomButton>
              )}
          </div>
        }
      >
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#1976d2' }}>
            Max Vault Level: {maxVaultLevel}
          </Typography>
          {sessionHelper.hasRole(
            PermittedRoles.VAULT_REWARDS_AND_DROP_RATES.UPDATE_ROLES
          ) && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleEditMaxVaultLevel}
                sx={{ minWidth: 'auto' }}
              >
                Edit
              </Button>
            )}
        </Box>
        {validationWarnings.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="warning">
              <AlertTitle>Vault Configuration Issues Detected</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Vault may not work properly due to the following reasons:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationWarnings.map((warning, index) => (
                  <li key={index}>
                    <Typography variant="body2">{warning}</Typography>
                  </li>
                ))}
              </ul>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                Expected: Levels 0-{maxVaultLevel} should each exist exactly once.
              </Typography>
            </Alert>
          </Box>
        )}
        <Table
          columns={columns}
          data={filteredList}
          actions={RowActions}
          highlightAltRow={true}
          onColumnClick={onColumnClick}
          actionHandlers={{
            handleDelete,
            handleEdit,
          }}
          hidePagination={true}
        />
      </ContentShell>

      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Max Vault Level</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Max Vault Level"
            type="number"
            fullWidth
            variant="outlined"
            value={editMaxVaultLevel}
            onChange={(e) => setEditMaxVaultLevel(Number(e.target.value))}
            inputProps={{ min: 0 }}
            helperText="The maximum level that can be reached in the vault (minimum: 0)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveMaxVaultLevel}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConsentDialog
        open={isOpenConsent}
        onClose={closeConsent}
        onConfirm={onConfirmDelete}
        itemName={constentDetails?.itemName}
      />
    </>
  );
};

export default VaultRewardConfigList;
